'use client';

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const MIN_DISPLAY_MS = 1300;
const MAX_WAIT_MS = 1800;

export default function AppEntryLoader() {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const startedAt = Date.now();
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleHide = () => {
      if (hideTimer) return;
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);
      hideTimer = setTimeout(() => setIsVisible(false), remaining);
    };

    const onReadyState = () => {
      if (document.readyState === 'interactive' || document.readyState === 'complete') {
        scheduleHide();
      }
    };

    onReadyState();
    document.addEventListener('readystatechange', onReadyState);
    fallbackTimer = setTimeout(scheduleHide, MAX_WAIT_MS);

    return () => {
      if (hideTimer) clearTimeout(hideTimer);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      document.removeEventListener('readystatechange', onReadyState);
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-[#07131f]"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_25%,rgba(36,99,235,0.2),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(13,148,136,0.18),transparent_42%),radial-gradient(circle_at_50%_85%,rgba(245,158,11,0.12),transparent_50%)]" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.07] hero-pattern" />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="relative z-10 w-[min(80vw,20rem)] rounded-2xl border border-white/10 bg-white/5 p-6 shadow-[0_16px_50px_rgba(0,0,0,0.35)] backdrop-blur-md"
          >
            <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/15">
              <motion.div
                className="h-full w-2/5 rounded-full bg-gradient-to-r from-cyan-300 via-blue-400 to-amber-300"
                animate={{ x: ['-120%', '220%'] }}
                transition={{ duration: 1.05, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>

            <div className="mt-4 flex items-center justify-center gap-2">
              {[0, 1, 2].map((dot) => (
                <motion.span
                  key={dot}
                  className="h-1.5 w-1.5 rounded-full bg-white/70"
                  animate={{ opacity: [0.25, 1, 0.25] }}
                  transition={{ duration: 0.9, repeat: Infinity, delay: dot * 0.12 }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
